<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { useFinanceApi } from '../composables/useFinanceApi'
import { formatMoney, toIsoDate } from '../utils/format'
import type { FinancialAccount } from '../types/finance'

const { t } = useI18n()
const router = useRouter()
const { listFinancialAccounts, createFinancialTransfer } = useFinanceApi()

const accounts = ref<FinancialAccount[]>([])
const loading = ref(true)
const saving = ref(false)
const loadError = ref('')
const formError = ref('')

const form = ref({
  source_account_id: '' as string | number,
  destination_account_id: '' as string | number,
  transfer_date: toIsoDate(),
  source_amount: 0,
  destination_amount: 0,
  exchange_rate: 1,
  fee_amount: null as number | null,
  fee_account_id: '' as string | number,
  reference: '',
  notes: '',
})

const activeAccounts = computed(() => accounts.value.filter((account) => account.is_active === 1))

function accountById(id: string | number): FinancialAccount | undefined {
  return accounts.value.find((account) => String(account.id) === String(id))
}

const sourceAccount = computed(() => accountById(form.value.source_account_id))
const destinationAccount = computed(() => accountById(form.value.destination_account_id))
const sameCurrency = computed(
  () =>
    !!sourceAccount.value &&
    !!destinationAccount.value &&
    sourceAccount.value.currency_code === destinationAccount.value.currency_code,
)

const validationError = computed(() => {
  if (!form.value.source_account_id) {
    return t('finance.common.requiredField', { field: t('finance.transfers.sourceAccount') })
  }
  if (!form.value.destination_account_id) {
    return t('finance.common.requiredField', { field: t('finance.transfers.destinationAccount') })
  }
  if (String(form.value.source_account_id) === String(form.value.destination_account_id)) {
    return t('finance.transfers.sameAccount')
  }
  if (!form.value.transfer_date) {
    return t('finance.common.requiredField', { field: t('finance.transfers.transferDate') })
  }
  if (Number(form.value.source_amount) <= 0) {
    return t('finance.common.requiredField', { field: t('finance.transfers.sourceAmount') })
  }
  if (Number(form.value.destination_amount) <= 0) {
    return t('finance.common.requiredField', { field: t('finance.transfers.destinationAmount') })
  }
  return ''
})

/** Same-currency transfers keep 1:1 amounts and an implied rate of 1. */
function syncAmounts() {
  if (sameCurrency.value) {
    form.value.exchange_rate = 1
    form.value.destination_amount = Number(form.value.source_amount)
  }
}

watch(
  () => form.value.source_amount,
  () => {
    syncAmounts()
  },
)

watch(
  () => [form.value.source_account_id, form.value.destination_account_id],
  () => {
    syncAmounts()
  },
)

watch(
  () => [form.value.destination_amount, form.value.exchange_rate],
  () => {
    if (sameCurrency.value) {
      const source = Number(form.value.source_amount)
      if (Number(form.value.destination_amount) !== source) {
        form.value.destination_amount = source
      }
      if (Number(form.value.exchange_rate) !== 1) form.value.exchange_rate = 1
    }
  },
)

async function load() {
  loading.value = true
  loadError.value = ''
  try {
    accounts.value = await listFinancialAccounts({ is_active: 1 })
  } catch (cause) {
    loadError.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function submit() {
  if (validationError.value) {
    formError.value = validationError.value
    return
  }
  saving.value = true
  formError.value = ''
  try {
    await createFinancialTransfer({
      source_account_id: form.value.source_account_id,
      destination_account_id: form.value.destination_account_id,
      transfer_date: form.value.transfer_date,
      source_amount: Number(form.value.source_amount),
      destination_amount: Number(form.value.destination_amount),
      exchange_rate: Number(form.value.exchange_rate) || 1,
      fee_amount: form.value.fee_amount ? Number(form.value.fee_amount) : undefined,
      fee_account_id: form.value.fee_amount ? form.value.fee_account_id || undefined : undefined,
      reference: form.value.reference.trim() || undefined,
      notes: form.value.notes.trim() || undefined,
    })
    await router.push('/treasury/transfers')
  } catch (cause) {
    formError.value = formatApiError(cause)
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <RouterLink class="back" to="/treasury/transfers">← {{ t('finance.transfers.title') }}</RouterLink>
    <h2>{{ t('finance.transfers.newTransfer') }}</h2>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="loadError" class="state-message error">
    <p>{{ loadError }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <form v-else class="form-card" @submit.prevent="submit">
    <h3>{{ t('finance.transfers.title') }}</h3>

    <div class="form-grid">
      <div class="field">
        <label for="transfer-source">{{ t('finance.transfers.sourceAccount') }}</label>
        <select id="transfer-source" v-model="form.source_account_id">
          <option value="">{{ t('finance.common.allAccounts') }}</option>
          <option v-for="account in activeAccounts" :key="account.id" :value="account.id">
            {{ account.name }} — {{ formatMoney(account.current_balance, account.currency_code) }}
          </option>
        </select>
      </div>
      <div class="field">
        <label for="transfer-destination">{{ t('finance.transfers.destinationAccount') }}</label>
        <select id="transfer-destination" v-model="form.destination_account_id">
          <option value="">{{ t('finance.common.allAccounts') }}</option>
          <option v-for="account in activeAccounts" :key="account.id" :value="account.id">
            {{ account.name }} — {{ formatMoney(account.current_balance, account.currency_code) }}
          </option>
        </select>
      </div>
      <div class="field">
        <label for="transfer-date">{{ t('finance.transfers.transferDate') }}</label>
        <input id="transfer-date" v-model="form.transfer_date" type="date">
      </div>
      <div class="field">
        <label for="transfer-source-amount">{{ t('finance.transfers.sourceAmount') }}</label>
        <input
          id="transfer-source-amount"
          v-model.number="form.source_amount"
          type="number"
          step="0.01"
        >
        <small v-if="sourceAccount">{{ sourceAccount.currency_code }}</small>
      </div>
      <div class="field">
        <label for="transfer-destination-amount">{{ t('finance.transfers.destinationAmount') }}</label>
        <input
          id="transfer-destination-amount"
          v-model.number="form.destination_amount"
          type="number"
          step="0.01"
          :disabled="sameCurrency"
        >
        <small v-if="destinationAccount">{{ destinationAccount.currency_code }}</small>
      </div>
      <div class="field">
        <label for="transfer-rate">{{ t('finance.common.exchangeRate') }}</label>
        <input
          id="transfer-rate"
          v-model.number="form.exchange_rate"
          type="number"
          step="0.000001"
          :disabled="sameCurrency"
        >
      </div>
      <div class="field">
        <label for="transfer-fee">{{ t('finance.transfers.feeAmount') }} ({{ t('finance.common.optional') }})</label>
        <input id="transfer-fee" v-model.number="form.fee_amount" type="number" step="0.01">
      </div>
      <div class="field">
        <label for="transfer-fee-account">{{ t('finance.transfers.feeAccount') }}</label>
        <select id="transfer-fee-account" v-model="form.fee_account_id">
          <option value="">{{ t('finance.common.none') }}</option>
          <option v-for="account in activeAccounts" :key="account.id" :value="account.id">
            {{ account.name }} — {{ account.currency_code }}
          </option>
        </select>
      </div>
      <div class="field">
        <label for="transfer-reference">{{ t('finance.common.reference') }}</label>
        <input id="transfer-reference" v-model="form.reference" type="text" autocomplete="off">
      </div>
      <div class="field field-wide">
        <label for="transfer-notes">{{ t('finance.common.notes') }}</label>
        <textarea id="transfer-notes" v-model="form.notes" />
      </div>
    </div>

    <p v-if="formError" class="error-inline">{{ formError }}</p>

    <div class="form-actions">
      <button class="btn-primary" type="submit" :disabled="saving">
        {{ saving ? t('finance.common.saving') : t('finance.common.save') }}
      </button>
      <RouterLink class="btn-secondary" to="/treasury/transfers">
        {{ t('finance.common.cancel') }}
      </RouterLink>
    </div>
  </form>
</template>
